// Skills: how agents handle a situation, written once and given to several
// (pantallas-abilities-y-skills, phase 3). Composed like KeysSecretsPage: a
// header with the one action, the list as the repeated element, and the
// confirmation for deleting outside the form so no dialog opens over another.

import * as React from 'react'
import { useQuery } from '@tanstack/react-query'
import { Pencil, Plus, Sparkles, Trash2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'

import { SkillDialog } from '@/components/skills/SkillDialog'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { EmptyState } from '@/components/ui/empty-state'
import { PageHeader } from '@/components/ui/page-header'
import { Switch } from '@/components/ui/switch'
import {
  serverMessage,
  useAbilitiesQuery,
  useCreateSkill,
  useDeleteSkill,
  useSkillsQuery,
  useUpdateSkill,
} from '@/queries/agents'
import { agentsService } from '@/services/agentsService'
import type { Skill, SkillPayload } from '@/types/agent'

export function SkillsPage() {
  const { t } = useTranslation()
  const { data: skills = [], isLoading } = useSkillsQuery()
  const { data: abilities = [] } = useAbilitiesQuery()
  const { data: agents = [] } = useQuery({ queryKey: ['agents', 'list'], queryFn: () => agentsService.listAgents() })
  const createSkill = useCreateSkill()
  const updateSkill = useUpdateSkill()
  const deleteSkill = useDeleteSkill()

  const [editing, setEditing] = React.useState<Skill | null>(null)
  const [formOpen, setFormOpen] = React.useState(false)
  const [serverError, setServerError] = React.useState<string | null>(null)
  const [toDelete, setToDelete] = React.useState<Skill | null>(null)

  const abilityName = (id: string) => abilities.find((a) => a.id === id)?.name ?? id
  const agentName = (id: string) => {
    const agent = agents.find((a) => a.id === id)
    return agent?.display_name || agent?.name || id
  }

  const openForm = (skill: Skill | null) => {
    setEditing(skill)
    setServerError(null)
    setFormOpen(true)
  }

  const save = async (payload: SkillPayload) => {
    setServerError(null)
    try {
      if (editing) {
        await updateSkill.mutateAsync({ id: editing.id, changes: payload })
        toast.success(t('skills_page.saved'))
      } else {
        await createSkill.mutateAsync(payload)
        toast.success(t('skills_page.created'))
      }
      setFormOpen(false)
    } catch (error) {
      setServerError(serverMessage(error) ?? t('skills_page.error'))
    }
  }

  const setEnabled = async (skill: Skill, isEnabled: boolean) => {
    try {
      await updateSkill.mutateAsync({ id: skill.id, changes: { is_enabled: isEnabled } })
    } catch (error) {
      toast.error(serverMessage(error) ?? t('skills_page.error'))
    }
  }

  const confirmDelete = async () => {
    if (!toDelete) return
    try {
      await deleteSkill.mutateAsync(toDelete.id)
      toast.success(t('skills_page.deleted'))
    } catch (error) {
      toast.error(serverMessage(error) ?? t('skills_page.error'))
    } finally {
      setToDelete(null)
    }
  }

  return (
    <div className="container mx-auto space-y-8 p-4 md:p-6">
      <PageHeader title={t('skills_page.title')} description={t('skills_page.subtitle')}>
        <Button size="lg" onClick={() => openForm(null)}>
          <Plus className="mr-2 h-4 w-4" />
          {t('skills_page.create')}
        </Button>
      </PageHeader>

      {isLoading ? (
        <p className="text-xs text-muted-foreground">{t('skills_page.loading')}</p>
      ) : skills.length === 0 ? (
        <EmptyState
          icon={<Sparkles className="h-5 w-5" />}
          title={t('skills_page.empty_title')}
          description={t('skills_page.empty_description')}
        />
      ) : (
        <div className="space-y-2">
          {skills.map((skill) => (
            <div key={skill.id} className="flex items-start justify-between gap-4 rounded-lg border bg-card p-4">
              <div className="min-w-0 space-y-1">
                <p className="truncate font-medium">{skill.name}</p>
                {skill.situation && <p className="text-xs text-muted-foreground">{skill.situation}</p>}
                <div className="flex flex-wrap gap-2">
                  {skill.ability_ids.map((id) => (
                    <Badge key={id} variant="secondary">{abilityName(id)}</Badge>
                  ))}
                </div>
                <p className="text-xs text-muted-foreground">
                  {skill.agent_ids.length === 0
                    ? t('skills_page.no_agents')
                    : t('skills_page.agents', { names: skill.agent_ids.map(agentName).join(', ') })}
                </p>
              </div>
              <div className="flex shrink-0 items-center gap-2">
                <Switch
                  checked={skill.is_enabled}
                  onCheckedChange={(on) => void setEnabled(skill, on)}
                  aria-label={t('skills_page.active', { name: skill.name })}
                />
                <Button variant="ghost" size="icon-xs" onClick={() => openForm(skill)} aria-label={t('skills_page.edit', { name: skill.name })}>
                  <Pencil className="h-4 w-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon-xs"
                  className="text-destructive hover:text-destructive"
                  onClick={() => setToDelete(skill)}
                  aria-label={t('skills_page.delete', { name: skill.name })}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      <SkillDialog
        open={formOpen}
        onOpenChange={setFormOpen}
        skill={editing}
        abilities={abilities}
        agents={agents}
        saving={createSkill.isPending || updateSkill.isPending}
        serverError={serverError}
        onSave={(payload) => void save(payload)}
      />

      <AlertDialog open={toDelete !== null} onOpenChange={(open) => !open && setToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('skills_page.delete_title')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('skills_page.delete_description', { name: toDelete?.name ?? '' })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('skills_page.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => void confirmDelete()}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {t('skills_page.confirm_delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
